import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Plus, Edit2, Trash2, Search, RefreshCw, Package, CheckCircle, AlertTriangle, Wrench, X, Monitor, Smartphone, Laptop, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ASSET_TYPES = ['Laptop', 'Mobile', 'Tablet', 'Desktop', 'Monitor', 'Keyboard', 'Mouse', 'Printer', 'Furniture', 'Vehicle', 'Other'];
const ASSET_STATUS = ['Available', 'Assigned', 'Under Repair', 'Retired', 'Lost'];

function AllAssets({ user, onLogout }) {
  const [assets, setAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Search & Filter
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  const [formData, setFormData] = useState({
    asset_name: '',
    asset_type: 'Laptop',
    serial_number: '',
    photo_url: '',
    purchase_date: '',
    purchase_cost: '',
    warranty_expiry: '',
    status: 'Available',
    condition: 'Good',
    location_id: '',
    description: '',
    vendor: ''
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [assetsRes, locationsRes, employeesRes] = await Promise.allSettled([
        axios.get(`${API}/assets`, { headers }),
        axios.get(`${API}/master-data/locations`, { headers }),
        axios.get(`${API}/employees`, { headers })
      ]);

      setAssets(assetsRes.status === 'fulfilled' ? (assetsRes.value.data.assets || assetsRes.value.data || []) : []);
      setLocations(locationsRes.status === 'fulfilled' ? (locationsRes.value.data.locations || []) : []);
      setEmployees(employeesRes.status === 'fulfilled' ? (employeesRes.value.data || []) : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter assets
  const filteredAssets = assets.filter(asset => {
    const matchesSearch = !searchTerm || 
      asset.asset_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assigned_to?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
    const matchesType = filterType === 'all' || asset.asset_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Statistics
  const stats = {
    total: assets.length,
    available: assets.filter(a => a.status === 'Available').length,
    assigned: assets.filter(a => a.status === 'Assigned').length,
    underRepair: assets.filter(a => a.status === 'Under Repair').length
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (selectedAsset) {
        await axios.put(`${API}/assets/${selectedAsset.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Asset updated successfully!');
      } else {
        await axios.post(`${API}/assets`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Asset created successfully!');
      }
      
      setShowForm(false);
      setSelectedAsset(null);
      resetForm();
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (asset) => {
    setSelectedAsset(asset);
    setFormData({
      asset_name: asset.asset_name || '',
      asset_type: asset.asset_type || 'Laptop',
      serial_number: asset.serial_number || '',
      photo_url: asset.photo_url || '',
      purchase_date: asset.purchase_date || '',
      purchase_cost: asset.purchase_cost || '',
      warranty_expiry: asset.warranty_expiry || '',
      status: asset.status || 'Available',
      condition: asset.condition || 'Good',
      location_id: asset.location_id || '',
      description: asset.description || '',
      vendor: asset.vendor || ''
    });
    setShowForm(true);
  };

  const handleAssign = async (asset) => {
    const employeeId = prompt('Enter Employee ID to assign:');
    if (!employeeId) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/${asset.id}/assign?employee_id=${employeeId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset assigned successfully!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUnassign = async (asset) => {
    if (!window.confirm('Are you sure you want to unassign this asset?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assets/${asset.id}/unassign`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset unassigned successfully!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`Delete asset "${asset.asset_name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assets/${asset.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Asset deleted successfully!');
      fetchAllData();
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      asset_name: '',
      asset_type: 'Laptop',
      serial_number: '',
      photo_url: '',
      purchase_date: '',
      purchase_cost: '',
      warranty_expiry: '',
      status: 'Available',
      condition: 'Good',
      location_id: '',
      description: '',
      vendor: ''
    });
    setSelectedAsset(null);
  };

  const getStatusColor = (status) => {
    const colors = {
      'Available': 'bg-green-100 text-green-700 border-l-green-500',
      'Assigned': 'bg-blue-100 text-blue-700 border-l-blue-500',
      'Under Repair': 'bg-yellow-100 text-yellow-700 border-l-yellow-500',
      'Retired': 'bg-gray-100 text-gray-700 border-l-gray-400',
      'Lost': 'bg-red-100 text-red-700 border-l-red-500'
    };
    return colors[status] || colors['Available'];
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'Available': 'bg-green-500 text-white',
      'Assigned': 'bg-blue-500 text-white',
      'Under Repair': 'bg-yellow-500 text-white',
      'Retired': 'bg-gray-500 text-white',
      'Lost': 'bg-red-500 text-white'
    };
    return colors[status] || colors['Available'];
  };

  const getAssetIcon = (type) => {
    switch (type) {
      case 'Laptop': return <Laptop className="w-5 h-5" />;
      case 'Mobile': case 'Tablet': return <Smartphone className="w-5 h-5" />;
      case 'Desktop': case 'Monitor': return <Monitor className="w-5 h-5" />;
      default: return <Package className="w-5 h-5" />;
    }
  };

  const canEdit = user?.role?.toLowerCase() === 'admin' || user?.role?.toLowerCase() === 'hr manager';

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>📦 Asset Management</h1>
            <p className="text-gray-600 mt-2">Track and manage company assets and equipment</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAllData}
              disabled={loading}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {canEdit && (
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                className="px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2"
                style={{backgroundColor: '#2b3380'}}
              >
                <Plus className="w-5 h-5" />
                Add Asset
              </button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full" style={{backgroundColor: '#e8eaf6'}}>
              <Package className="w-6 h-6" style={{color: '#2b3380'}} />
            </div>
            <div>
              <p className="text-2xl font-bold" style={{color: '#2b3380'}}>{stats.total}</p>
              <p className="text-sm text-gray-500">Total Assets</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.available}</p>
              <p className="text-sm text-gray-500">Available</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-blue-100">
              <UserCheck className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{stats.assigned}</p>
              <p className="text-sm text-gray-500">Assigned</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
            <div className="p-3 rounded-full bg-yellow-100">
              <Wrench className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-yellow-600">{stats.underRepair}</p>
              <p className="text-sm text-gray-500">Under Repair</p>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search assets by name, serial number, or assignee..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              {ASSET_STATUS.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {ASSET_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Assets List */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
            <p className="mt-3 text-gray-500">Loading assets...</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <p className="text-xl font-medium text-gray-500 mb-2">No assets found</p>
            <p className="text-gray-400">
              {searchTerm || filterStatus !== 'all' || filterType !== 'all'
                ? 'Try different search terms or filters' 
                : canEdit ? 'Click "Add Asset" to create one' : 'No assets available'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAssets.map((asset) => (
              <div 
                key={asset.id} 
                className={`bg-white rounded-lg shadow-md p-6 border-l-4 hover:shadow-lg transition-shadow ${getStatusColor(asset.status)}`}
              >
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="p-2 bg-gray-100 rounded-lg">
                        {getAssetIcon(asset.asset_type)}
                      </span>
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>{asset.asset_name}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(asset.status)}`}>
                        {asset.status}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {asset.asset_type}
                      </span>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(asset)}
                        className="p-2 hover:bg-blue-50 rounded text-blue-600"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(asset)}
                        className="p-2 hover:bg-red-50 rounded text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                  <div><strong>Serial:</strong> {asset.serial_number || 'N/A'}</div>
                  <div><strong>Condition:</strong> {asset.condition || 'N/A'}</div>
                  <div><strong>Vendor:</strong> {asset.vendor || 'N/A'}</div>
                  <div><strong>Cost:</strong> ₹{asset.purchase_cost ? Number(asset.purchase_cost).toLocaleString() : 'N/A'}</div>
                </div>
                
                {asset.description && (
                  <p className="text-gray-700 mb-3">{asset.description}</p>
                )}
                
                <div className="flex flex-wrap justify-between items-center text-sm text-gray-500 pt-3 border-t">
                  <div className="flex items-center gap-4">
                    {asset.assigned_to && (
                      <span className="flex items-center gap-1">
                        <UserCheck className="w-4 h-4" />
                        Assigned to: <strong>{asset.assigned_to}</strong>
                      </span>
                    )}
                    {asset.warranty_expiry && (
                      <span>Warranty: {new Date(asset.warranty_expiry).toLocaleDateString('en-IN')}</span>
                    )}
                  </div>
                  {canEdit && (
                    <div className="flex gap-2 mt-2 md:mt-0">
                      {asset.status === 'Available' && (
                        <button
                          onClick={() => handleAssign(asset)}
                          className="px-3 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs font-medium flex items-center gap-1"
                        >
                          <UserCheck className="w-3 h-3" /> Assign
                        </button>
                      )}
                      {asset.status === 'Assigned' && (
                        <button
                          onClick={() => handleUnassign(asset)}
                          className="px-3 py-1 bg-orange-100 text-orange-700 rounded hover:bg-orange-200 text-xs font-medium flex items-center gap-1"
                        >
                          <UserX className="w-3 h-3" /> Unassign
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedAsset ? '✏️ Edit Asset' : '📦 Add New Asset'}
                  </h2>
                  <button onClick={() => { setShowForm(false); resetForm(); }} className="p-2 hover:bg-gray-100 rounded-lg">
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Asset Name *</label>
                        <input
                          type="text"
                          value={formData.asset_name}
                          onChange={(e) => setFormData({...formData, asset_name: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Asset Type *</label>
                        <select
                          value={formData.asset_type}
                          onChange={(e) => setFormData({...formData, asset_type: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {ASSET_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Serial Number *</label>
                        <input
                          type="text"
                          value={formData.serial_number}
                          onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Status</label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({...formData, status: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          {ASSET_STATUS.map(status => (
                            <option key={status} value={status}>{status}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Purchase Date</label>
                        <input
                          type="date"
                          value={formData.purchase_date}
                          onChange={(e) => setFormData({...formData, purchase_date: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Purchase Cost (₹)</label>
                        <input
                          type="number"
                          value={formData.purchase_cost}
                          onChange={(e) => setFormData({...formData, purchase_cost: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Warranty Expiry</label>
                        <input
                          type="date"
                          value={formData.warranty_expiry}
                          onChange={(e) => setFormData({...formData, warranty_expiry: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Vendor</label>
                        <input
                          type="text"
                          value={formData.vendor}
                          onChange={(e) => setFormData({...formData, vendor: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2">Condition</label>
                        <select
                          value={formData.condition}
                          onChange={(e) => setFormData({...formData, condition: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="Excellent">Excellent</option>
                          <option value="Good">Good</option>
                          <option value="Fair">Fair</option>
                          <option value="Poor">Poor</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-2">Location</label>
                        <select
                          value={formData.location_id}
                          onChange={(e) => setFormData({...formData, location_id: e.target.value})}
                          className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Location</option>
                          {locations.map(loc => (
                            <option key={loc.id} value={loc.id}>
                              {loc.name} - {loc.city}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        rows="3"
                        placeholder="Additional details about the asset..."
                      />
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-700">
                        <strong>Note:</strong> Serial number should be unique for each asset. Assets can be assigned to employees after creation.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="px-6 py-3 text-white rounded-lg font-medium flex items-center gap-2"
                        style={{backgroundColor: submitting ? '#9e9e9e' : '#2b3380'}}
                      >
                        <Package className="w-5 h-5" />
                        {submitting ? 'Saving...' : (selectedAsset ? 'Update Asset' : 'Add Asset')}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); resetForm(); }}
                        className="px-6 py-3 border rounded-lg hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default AllAssets;
